import NativeEventEmitterModule from 'react-native/Libraries/EventEmitter/NativeEventEmitter'
import { NativeModules } from 'react-native'

export type NoraUtterance = {
  id: string
  filePath: string
  durationMs: number
  byteCount: number
}

type NoraAudioCaptureCallbacks = {
  onSpeechStart?: () => void
  onUtterance?: (utterance: NoraUtterance) => void
  onError?: (error: Error) => void
  // Normalized 0..1 mic loudness, emitted by the native modules at ~20Hz while
  // capture is running (including during silence, where it reports near zero).
  onLevel?: (level: number) => void
}

type NativeSubscription = {
  remove: () => void
}

type NativeEventEmitterConstructor = new (nativeModule: unknown) => {
  addListener: (eventName: string, listener: (payload: unknown) => void) => NativeSubscription
}

type NativeNoraAudioCaptureModule = {
  start: () => Promise<void>
  stop: () => Promise<void>
  // Optional so a JS bundle running against an older native binary degrades to the
  // permissive thresholds instead of crashing on an undefined method.
  setEchoGuard?: (active: boolean) => Promise<void>
}

const nativeModule = NativeModules.NoraAudioCapture as NativeNoraAudioCaptureModule | null
const NativeEventEmitter = (NativeEventEmitterModule as unknown) as NativeEventEmitterConstructor

// NativeEventEmitter throws on iOS when constructed with a null module, so build it
// lazily and only once the module is known to be linked.
let emitterInstance: InstanceType<NativeEventEmitterConstructor> | null = null

const getEmitter = (module: NativeNoraAudioCaptureModule) => {
  if (!emitterInstance) {
    emitterInstance = new NativeEventEmitter(module)
  }

  return emitterInstance
}

let active = false
let subscriptions: NativeSubscription[] = []

const removeSubscriptions = () => {
  subscriptions.forEach((subscription) => subscription.remove())
  subscriptions = []
}

const unavailableError = () =>
  new Error('Nora audio capture native module is unavailable. Rebuild the app.')

const payloadLevel = (payload: unknown) => {
  if (payload && typeof payload === 'object' && 'level' in payload) {
    const level = (payload as { level?: unknown }).level
    if (typeof level === 'number' && Number.isFinite(level)) {
      return Math.min(1, Math.max(0, level))
    }
  }

  return null
}

const buildError = (payload: unknown) => {
  if (payload && typeof payload === 'object' && 'message' in payload) {
    return new Error(String((payload as { message?: unknown }).message))
  }

  return new Error('Nora audio capture failed')
}

export const NoraAudioCapture = {
  start: async (callbacks: NoraAudioCaptureCallbacks = {}) => {
    if (active) {
      return
    }

    if (!nativeModule) {
      const error = unavailableError()
      throw error
    }

    // Claim the active flag before awaiting the native call so a second start()
    // issued before the first resolves cannot register duplicate listeners.
    active = true

    const emitter = getEmitter(nativeModule)

    subscriptions = [
      emitter.addListener('noraSpeechStart', () => callbacks.onSpeechStart?.()),
      emitter.addListener('noraUtteranceReady', (utterance: unknown) =>
        callbacks.onUtterance?.(utterance as NoraUtterance),
      ),
      emitter.addListener('noraCaptureError', (payload: unknown) =>
        callbacks.onError?.(buildError(payload)),
      ),
    ]

    if (callbacks.onLevel) {
      subscriptions.push(
        emitter.addListener('noraInputLevel', (payload: unknown) => {
          const level = payloadLevel(payload)
          if (level !== null) {
            callbacks.onLevel?.(level)
          }
        }),
      )
    }

    try {
      await nativeModule.start()
    } catch (error) {
      active = false
      removeSubscriptions()
      throw error
    }
  },

  // Tightens the native speech gate while Nora is speaking so her voice leaking past
  // AEC cannot trigger a barge-in, and relaxes it again the moment she stops. Failures
  // are swallowed: the guard is a tuning refinement, and a rejected call must never take
  // down the voice session.
  setEchoGuard: async (guardActive: boolean) => {
    try {
      await nativeModule?.setEchoGuard?.(guardActive)
    } catch {
      // Intentionally ignored; see above.
    }
  },

  stop: async () => {
    const wasActive = active

    active = false
    removeSubscriptions()

    if (!wasActive) {
      return
    }

    if (!nativeModule) {
      return
    }

    await nativeModule.stop()
  },
}
